/**
 * 選擇系統 - 處理需要玩家決策的效果
 * 
 * 效果類型：
 * 1. 選項選擇：「1)抽2張牌 或 2)使你墓地1單位回手牌」
 * 2. 目標選擇：「選擇1個敵方單位將其破壞」
 * 3. 卡牌選擇：「從牌庫尋找1張卡牌加入手牌」
 * 4. 位置選擇：「選擇1個空位召喚單位」
 */
import type {
  GameState,
  Card,
  CardDefinition,
  BattlefieldPosition,
} from '../../types';

// ============================================================
// 選擇類型定義
// ============================================================

/**
 * 選擇類型
 */
export type ChoiceType =
  | 'option'      // 選項選擇（A 或 B）
  | 'target'      // 目標選擇（選擇單位）
  | 'card'        // 卡牌選擇（從區域選牌）
  | 'position'    // 位置選擇（選擇格子）
  | 'number';     // 數字選擇（選擇數量）

/**
 * 選項定義
 */
export interface ChoiceOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * 目標過濾器
 */
export interface TargetFilter {
  side?: 'self' | 'enemy' | 'both' | 'any';
  zone?: 'battlefield' | 'hand' | 'graveyard' | 'deck' | 'forest' | 'extra';
  row?: 'front' | 'back' | 'any';
  faction?: string;
  type?: 'unit' | 'spell';
  name?: string;
  nameContains?: string;
  minAtk?: number;
  maxAtk?: number;
  hasKeyword?: string;
  excludeIds?: string[];
}

/**
 * 待決選擇
 */
export interface PendingChoice {
  id: string;
  type: ChoiceType;
  sourceCardId: string;
  sourceCardName: string;
  effectText: string;
  playerId: string;
  
  // 選項選擇
  options?: ChoiceOption[];
  
  // 目標/卡牌選擇
  filter?: TargetFilter;
  minCount?: number;
  maxCount?: number;
  
  // 位置選擇
  validPositions?: BattlefieldPosition[];
  
  // 數字選擇
  minValue?: number;
  maxValue?: number;
  
  // 是否可選（可以選擇不執行）
  optional?: boolean;
  
  // 回調資訊（用於繼續執行）
  continuationData?: Record<string, unknown>;
  
  // 時間限制（毫秒）
  timeoutMs?: number;
  createdAt: number;
}

/**
 * 選擇結果
 */
export interface ChoiceResult {
  choiceId: string;
  type: ChoiceType;
  
  // 選項結果
  selectedOptionId?: string;
  
  // 目標/卡牌結果
  selectedCardIds?: string[];
  
  // 位置結果
  selectedPosition?: BattlefieldPosition;
  
  // 數字結果
  selectedNumber?: number;
  
  // 是否取消（對於 optional 的選擇）
  cancelled?: boolean;
}

/**
 * 選擇請求（用於 handler 請求選擇）
 */
export interface ChoiceRequest {
  type: ChoiceType;
  effectText: string;
  options?: Omit<ChoiceOption, 'id'>[];
  filter?: TargetFilter;
  minCount?: number;
  maxCount?: number;
  validPositions?: BattlefieldPosition[];
  minValue?: number;
  maxValue?: number;
  optional?: boolean;
  continuationData?: Record<string, unknown>;
}

// ============================================================
// 選擇管理器
// ============================================================

/**
 * 選擇管理器 - 管理待決選擇的佇列
 */
export class ChoiceManager {
  private pendingChoices: Map<string, PendingChoice> = new Map();
  private choiceIdCounter = 0;

  /**
   * 創建新選擇
   */
  createChoice(
    request: ChoiceRequest,
    sourceCard: Card,
    sourceCardName: string,
    playerId: string
  ): PendingChoice {
    const id = `choice_${String(Date.now())}_${String(this.choiceIdCounter++)}`;
    
    const choice: PendingChoice = {
      id,
      type: request.type,
      sourceCardId: sourceCard.id,
      sourceCardName,
      effectText: request.effectText,
      playerId,
      options: request.options?.map((opt, idx) => ({
        ...opt,
        id: `opt_${String(idx)}`,
      })),
      filter: request.filter,
      minCount: request.minCount ?? 1,
      maxCount: request.maxCount ?? 1,
      validPositions: request.validPositions,
      minValue: request.minValue,
      maxValue: request.maxValue,
      optional: request.optional,
      continuationData: request.continuationData,
      createdAt: Date.now(),
    };

    this.pendingChoices.set(id, choice);
    return choice;
  }

  /**
   * 取得待決選擇
   */
  getPendingChoice(choiceId: string): PendingChoice | undefined {
    return this.pendingChoices.get(choiceId);
  }

  /**
   * 取得玩家的待決選擇
   */
  getPlayerPendingChoices(playerId: string): PendingChoice[] {
    return Array.from(this.pendingChoices.values())
      .filter(c => c.playerId === playerId);
  }

  /**
   * 取得所有待決選擇
   */
  getAllPendingChoices(): PendingChoice[] {
    return Array.from(this.pendingChoices.values());
  }

  /**
   * 是否有待決選擇
   */
  hasPendingChoices(): boolean {
    return this.pendingChoices.size > 0;
  }

  /**
   * 移除選擇
   */
  removeChoice(choiceId: string): void {
    this.pendingChoices.delete(choiceId);
  }

  /**
   * 清除所有選擇
   */
  clearAll(): void {
    this.pendingChoices.clear();
  }

  /**
   * 清除過期選擇
   */
  clearExpired(timeoutMs: number = 60000): PendingChoice[] {
    const now = Date.now();
    const expired: PendingChoice[] = [];
    
    for (const [id, choice] of this.pendingChoices) {
      const timeout = choice.timeoutMs ?? timeoutMs;
      if (now - choice.createdAt > timeout) {
        expired.push(choice);
        this.pendingChoices.delete(id);
      }
    }
    
    return expired;
  }
}

// ============================================================
// 選擇驗證器
// ============================================================

/**
 * 驗證選擇結果是否合法
 */
export class ChoiceValidator {
  private definitions: Map<string, CardDefinition>;

  constructor(definitions: Map<string, CardDefinition>) {
    this.definitions = definitions;
  }

  /**
   * 驗證選擇結果
   */
  validate(
    state: GameState,
    choice: PendingChoice,
    result: ChoiceResult
  ): { valid: boolean; error?: string } {
    if (result.cancelled && choice.optional) {
      return { valid: true };
    }

    switch (choice.type) {
      case 'option':
        return this.validateOption(choice, result);
      case 'target':
      case 'card':
        return this.validateCardSelection(state, choice, result);
      case 'position':
        return this.validatePosition(state, choice, result);
      case 'number':
        return this.validateNumber(choice, result);
      default:
        return { valid: false, error: '未知的選擇類型' };
    }
  }

  private validateOption(
    choice: PendingChoice,
    result: ChoiceResult
  ): { valid: boolean; error?: string } {
    if (!result.selectedOptionId) {
      return { valid: false, error: '未選擇選項' };
    }
    
    const option = choice.options?.find(o => o.id === result.selectedOptionId);
    if (!option) {
      return { valid: false, error: '無效的選項' };
    }
    
    if (option.disabled) {
      return { valid: false, error: option.disabledReason ?? '該選項已禁用' };
    }
    
    return { valid: true };
  }

  private validateCardSelection(
    state: GameState,
    choice: PendingChoice,
    result: ChoiceResult
  ): { valid: boolean; error?: string } {
    const selectedIds = result.selectedCardIds ?? [];
    const minCount = choice.minCount ?? 1;
    const maxCount = choice.maxCount ?? 1;
    
    if (selectedIds.length < minCount) {
      return { valid: false, error: `至少需要選擇 ${String(minCount)} 張卡牌` };
    }
    
    if (selectedIds.length > maxCount) {
      return { valid: false, error: `最多只能選擇 ${String(maxCount)} 張卡牌` };
    }
    
    // 驗證每張卡牌是否符合過濾條件
    const filter = choice.filter;
    if (filter) {
      const validCards = this.getValidCards(state, choice.playerId, filter);
      const validIds = new Set(validCards.map(c => c.id));
      
      for (const id of selectedIds) {
        if (!validIds.has(id)) {
          return { valid: false, error: `卡牌 ${id} 不符合選擇條件` };
        }
      }
    }
    
    return { valid: true };
  }

  private validatePosition(
    _state: GameState,
    choice: PendingChoice,
    result: ChoiceResult
  ): { valid: boolean; error?: string } {
    if (!result.selectedPosition) {
      return { valid: false, error: '未選擇位置' };
    }
    
    const pos = result.selectedPosition;
    const validPositions = choice.validPositions;
    
    if (validPositions && validPositions.length > 0) {
      const isValid = validPositions.some(
        p => p.side === pos.side && p.row === pos.row && p.column === pos.column
      );
      if (!isValid) {
        return { valid: false, error: '所選位置不在有效範圍內' };
      }
    }
    
    return { valid: true };
  }

  private validateNumber(
    choice: PendingChoice,
    result: ChoiceResult
  ): { valid: boolean; error?: string } {
    const num = result.selectedNumber;
    
    if (num === undefined) {
      return { valid: false, error: '未選擇數字' };
    }
    
    if (choice.minValue !== undefined && num < choice.minValue) {
      return { valid: false, error: `數字不能小於 ${String(choice.minValue)}` };
    }
    
    if (choice.maxValue !== undefined && num > choice.maxValue) {
      return { valid: false, error: `數字不能大於 ${String(choice.maxValue)}` };
    }
    
    return { valid: true };
  }

  /**
   * 取得符合過濾條件的卡牌
   */
  getValidCards(
    state: GameState,
    playerId: string,
    filter: TargetFilter
  ): Card[] {
    const isPlayer1 = playerId === state.player1.id;
    const cards: Card[] = [];
    
    // 根據 side 決定要搜尋哪邊
    const searchSelf = filter.side !== 'enemy';
    const searchEnemy = filter.side !== 'self';
    
    const selfPlayer = isPlayer1 ? state.player1 : state.player2;
    const enemyPlayer = isPlayer1 ? state.player2 : state.player1;
    // 收集卡牌
    const collectFromZone = (
      zone: Card[],
      zoneSide: 'self' | 'enemy'
    ) => {
      if ((zoneSide === 'self' && searchSelf) || (zoneSide === 'enemy' && searchEnemy)) {
        cards.push(...zone);
      }
    };
    
    const collectFromBattlefield = (
      side: typeof state.battlefield.player1Side,
      zoneSide: 'self' | 'enemy',
      sideKey: 'player1' | 'player2'
    ) => {
      if ((zoneSide === 'self' && searchSelf) || (zoneSide === 'enemy' && searchEnemy)) {
        const checkRow = (
          line: typeof side.frontLine,
          row: 'front' | 'back'
        ) => {
          if (filter.row && filter.row !== 'any' && filter.row !== row) return;
          
          line.forEach((slot, idx) => {
            if (slot.unit) {
              cards.push({
                ...slot.unit,
                position: { side: sideKey, row, column: (idx + 1) as 1 | 2 | 3 | 4 | 5 },
              });
            }
          });
        };
        
        checkRow(side.frontLine, 'front');
        checkRow(side.backLine, 'back');
      }
    };
    
    // 根據 zone 收集卡牌
    switch (filter.zone) {
      case 'battlefield':
        collectFromBattlefield(state.battlefield.player1Side, isPlayer1 ? 'self' : 'enemy', 'player1');
        collectFromBattlefield(state.battlefield.player2Side, isPlayer1 ? 'enemy' : 'self', 'player2');
        break;
      case 'hand':
        collectFromZone(selfPlayer.hand, 'self');
        collectFromZone(enemyPlayer.hand, 'enemy');
        break;
      case 'graveyard':
        collectFromZone(selfPlayer.graveyard, 'self');
        collectFromZone(enemyPlayer.graveyard, 'enemy');
        break;
      case 'deck':
        collectFromZone(selfPlayer.deck, 'self');
        collectFromZone(enemyPlayer.deck, 'enemy');
        break;
      case 'forest':
        collectFromZone(selfPlayer.forest, 'self');
        collectFromZone(enemyPlayer.forest, 'enemy');
        break;
      case 'extra':
        collectFromZone(selfPlayer.extra ?? [], 'self');
        collectFromZone(enemyPlayer.extra ?? [], 'enemy');
        break;
      default:
        // 所有區域
        collectFromBattlefield(state.battlefield.player1Side, isPlayer1 ? 'self' : 'enemy', 'player1');
        collectFromBattlefield(state.battlefield.player2Side, isPlayer1 ? 'enemy' : 'self', 'player2');
        collectFromZone(selfPlayer.hand, 'self');
        collectFromZone(selfPlayer.graveyard, 'self');
    }
    
    // 過濾卡牌
    return cards.filter(card => this.matchesFilter(card, filter));
  }

  private matchesFilter(card: Card, filter: TargetFilter): boolean {
    const def = this.definitions.get(card.definitionId);
    
    // 排除特定 ID
    if (filter.excludeIds?.includes(card.id)) {
      return false;
    }
    
    // 種族過濾
    if (filter.faction && def?.faction !== filter.faction) {
      return false;
    }
    
    // 類型過濾
    if (filter.type && def?.type !== filter.type) {
      return false;
    }
    
    // 名稱過濾
    if (filter.name) {
      const name = def?.nameZh ?? def?.name ?? '';
      if (name !== filter.name) {
        return false;
      }
    }
    
    // 名稱包含過濾
    if (filter.nameContains) {
      const name = def?.nameZh ?? def?.name ?? '';
      if (!name.includes(filter.nameContains)) {
        return false;
      }
    }
    
    // ATK 過濾
    if (def) {
      const atk = (def.atk ?? 0) + (card.atkModifier ?? 0);
      if (filter.minAtk !== undefined && atk < filter.minAtk) {
        return false;
      }
      if (filter.maxAtk !== undefined && atk > filter.maxAtk) {
        return false;
      }
    }
    
    return true;
  }
}

// ============================================================
// 選擇解析器 - 從效果文字解析選擇需求
// ============================================================

export interface ParsedChoiceRequirement {
  hasChoice: boolean;
  type?: ChoiceType;
  request?: ChoiceRequest;
}

/**
 * 從效果文字解析選擇需求
 */
export function parseChoiceRequirement(effectText: string): ParsedChoiceRequirement {
  // 1) 或 2) 格式的選項選擇
  const orMatch = effectText.match(/1[)）]([^或]+)或\s*2[)）]([^。]+)/);
  if (orMatch) {
    return {
      hasChoice: true,
      type: 'option',
      request: {
        type: 'option',
        effectText,
        options: [
          { label: orMatch[1].trim() },
          { label: orMatch[2].trim() },
        ],
      },
    };
  }

  // 「選擇」目標
  const selectMatch = effectText.match(/選擇(\d*)個?(敵方|你|友方)?([^單位]*單位|卡牌)/);
  if (selectMatch) {
    const count = selectMatch[1] ? parseInt(selectMatch[1], 10) : 1;
    const side = selectMatch[2] === '敵方' ? 'enemy' : selectMatch[2] ? 'self' : 'both';
    
    return {
      hasChoice: true,
      type: 'target',
      request: {
        type: 'target',
        effectText,
        filter: {
          side,
          zone: 'battlefield',
        },
        minCount: count,
        maxCount: count,
      },
    };
  }

  // 「從牌庫尋找」
  const searchMatch = effectText.match(/從(你)?牌庫尋找(\d*)張?([^並加入]+)並?加入/);
  if (searchMatch) {
    const count = searchMatch[2] ? parseInt(searchMatch[2], 10) : 1;
    const targetName = searchMatch[3].trim();
    
    return {
      hasChoice: true,
      type: 'card',
      request: {
        type: 'card',
        effectText,
        filter: {
          side: 'self',
          zone: 'deck',
          nameContains: targetName !== '卡牌' ? targetName : undefined,
        },
        minCount: count,
        maxCount: count,
      },
    };
  }

  // 「使你墓地X回手牌」（選擇墓地卡牌）
  const graveyardMatch = effectText.match(/使你墓地(\d*)([^回]+)?回手牌/);
  if (graveyardMatch) {
    const count = graveyardMatch[1] ? parseInt(graveyardMatch[1], 10) : 1;
    const targetDesc = graveyardMatch[2].trim();
    
    return {
      hasChoice: true,
      type: 'card',
      request: {
        type: 'card',
        effectText,
        filter: {
          side: 'self',
          zone: 'graveyard',
          nameContains: targetDesc,
        },
        minCount: count,
        maxCount: count,
      },
    };
  }

  // 「宣言」（選擇宣告）
  if (effectText.includes('宣言')) {
    return {
      hasChoice: true,
      type: 'option',
      request: {
        type: 'option',
        effectText,
        options: [], // 需要根據具體情況動態生成
        optional: false,
      },
    };
  }

  return { hasChoice: false };
}

// ============================================================
// 工廠函數
// ============================================================

let globalChoiceManager: ChoiceManager | null = null;

export function getChoiceManager(): ChoiceManager {
  if (!globalChoiceManager) {
    globalChoiceManager = new ChoiceManager();
  }
  return globalChoiceManager;
}

export function createChoiceValidator(
  definitions: Map<string, CardDefinition>
): ChoiceValidator {
  return new ChoiceValidator(definitions);
}
